import { PropertyLookUpModel } from "app/spaceStone/properties/addProperty/models/propertyLookupModels";
import { UserLookUpModelWithStatus } from "app/spaceStone/properties/addProperty/models/userLookupModels";
import { Country } from "components/userDetails/interfaces/country";
import { httpGet } from "shared/api/apiHandler";
import urls from "shared/api/spaceStone/urls";
import { EmployedStatusLookup } from "app/spaceStone/myProfile/about/interfaces";
import { PrimaryContactLookup } from "app/spaceStone/companyDetails/interfaces";
import { AgencyLookup, BaseLookup } from "shared/models/lookups";
import { AddressesLookup } from "shared/models/spaceStone/lookup/addressesLookup";
import { CompaniesHousesApiProps, CompaniesHousesLookup } from "shared/models/spaceStone/lookup/companiesHousesLookup";
import { AccountTypeLookup } from "shared/models/spaceStone/lookup/accountTypeLookup";
import { ID } from "shared/interfaces/common";
import { LookUpModelWithEmail } from "app/spaceStone/tenant/membership/models/membership";
import { FreeTenantLookUp } from "shared/models/spaceStone/lookup/tenantLookup";

export interface HonestyQuestionsLookup {
    id: number;
    text: string;
}

export const getHonestyQuestions = async (): Promise<HonestyQuestionsLookup[]> => {
    const result = await httpGet<HonestyQuestionsLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.HONESTY_QUESTION
    );
    return result.data;
};

export const getCountries = async (): Promise<Country[]> => {
    const result = await httpGet<Country[]>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.COUNTRIES);
    return result.data;
};

export const getUserStatuses = async (): Promise<BaseLookup[]> => {
    const result = await httpGet<Country[]>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.USER_STATUSES);
    return result.data;
};

export const getTenancyStatuses = async (): Promise<BaseLookup[]> => {
    const result = await httpGet<Country[]>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.TENANCY_STATUSES);
    return result.data;
};

export const getEmploymentStatuses = async (): Promise<EmployedStatusLookup[]> => {
    const result = await httpGet<Country[]>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.EMPLOYMENT_STATUSES);
    return result.data;
};

export const getClaimStatuses = async (): Promise<BaseLookup[]> => {
    const result = await httpGet<Country[]>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.CLAIM_STATUSES);
    return result.data;
};

export const getPrimaryContacts = async (companyId: string, searchText: string): Promise<PrimaryContactLookup[]> => {
    const result = await httpGet<PrimaryContactLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.PRIMARY_CONTACTS,
        {
            params: {
                companyId,
                searchText,
            },
        }
    );

    return result.data;
};

export const getCompaniesHouses = async (query: string): Promise<CompaniesHousesLookup[]> => {
    const result = await httpGet<CompaniesHousesApiProps[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.COMPANIES_HOUSES,
        {
            params: { query },
        }
    );

    return result.data.map(item => ({
        companyName: item.companyName,
        address: item.address,
        addressString: item.addressString,
        companyRegistrationNumber: item.companyNumber,
    }));
};

export const getAccountTypes = async (): Promise<AccountTypeLookup[]> => {
    const result = await httpGet<AccountTypeLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.ADMINISTRATION.USERS.ACCOUNT_TYPES
    );

    return result.data;
};

export const getDeactivationReasons = async (): Promise<BaseLookup[]> => {
    const result = await httpGet<BaseLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.DEACTIVATION_REASONS
    );

    return result.data;
};

export const getAgencies = async (searchText?: string, loadAllItems?: boolean): Promise<Array<AgencyLookup>> => {
    const result = await httpGet<Array<AgencyLookup>>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.AGENCIES, {
        params: {
            searchText,
            loadAllItems,
        },
    });

    return result.data;
};

export const getCompanies = async (searchText?: string, loadAllItems?: boolean): Promise<Array<AgencyLookup>> => {
    const result = await httpGet<Array<AgencyLookup>>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.COMPANIES, {
        params: {
            searchText,
            loadAllItems,
        },
    });

    return result.data;
};

export const getBranches = async (searchText?: string): Promise<Array<BaseLookup>> => {
    const result = await httpGet<Array<BaseLookup>>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.BRANCHES, {
        params: {
            searchText,
            includeAgency: true,
            loadAllItems: true,
        },
    });

    return result.data;
};

export const getAgencyBranches = async (agencyId: number, searchText?: string): Promise<BaseLookup[]> => {
    const result = await httpGet<BaseLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.AGENCY_BRANCHES(agencyId),
        {
            params: { searchText, includeAgency: true, loadAllItems: true },
        }
    );

    return result.data;
};

export const getAddresses = async (query: string): Promise<AddressesLookup[]> => {
    const result = await httpGet<AddressesLookup[]>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.ADDRESSES, {
        params: { query },
    });

    return result.data;
};

export const getLandlords = async (searchText: string): Promise<UserLookUpModelWithStatus[]> => {
    const result = await httpGet<UserLookUpModelWithStatus[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.LANDLORDS,
        {
            params: { searchText },
        }
    );

    return result.data;
};

export const getAgencyLandlords = async (searchText: string): Promise<UserLookUpModelWithStatus[]> => {
    const result = await httpGet<UserLookUpModelWithStatus[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.AGENCY_LANDLORDS,
        {
            params: { searchText },
        }
    );

    return result.data;
};

export const getLandlordProperties = async (landlordId: number, searchText: string): Promise<PropertyLookUpModel[]> => {
    const result = await httpGet<PropertyLookUpModel[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.LANDLORD_PROPERTIES(landlordId),
        {
            params: { searchText },
        }
    );

    return result.data;
};

export const getClaimStatusLookUp = async (): Promise<BaseLookup[]> => {
    const result = await httpGet<BaseLookup[]>(window.APP_SETTINGS.spaceStoneApiBaseUri, urls.LOOKUP.CLAIM_STATUSES);

    return result.data;
};

export const getTenantsLookup = async (propertyId: ID): Promise<BaseLookup[]> => {
    const result = await httpGet<BaseLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.TENANCY_TENANTS(propertyId)
    );
    return result.data;
};

export const getFrequenciesOfRent = async (): Promise<BaseLookup[]> => {
    const result = await httpGet<BaseLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.FREQUENCIES_OF_RENT
    );
    return result.data;
};

export const getPossibleEndOfTenancyInitiators = async (
    searchText: string,
    endOfTenancyTypeId?: number,
    tenancyID?: number,
    loadAllItems?: boolean
): Promise<BaseLookup[]> => {
    const result = await httpGet<BaseLookup[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.POSSIBLE_END_OF_TENANCY_INITIATORS,
        {
            params: {
                searchText,
                endOfTenancyTypeId,
                tenancyID,
                loadAllItems,
            },
        }
    );
    return result.data;
};

export const getTenantsThatShouldConfirmMembership = async (tenancyId: ID): Promise<Array<LookUpModelWithEmail>> => {
    const result = await httpGet<Array<LookUpModelWithEmail>>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.TENANCY_TENANTS_THAT_SHOULD_CONFIRM_MEMBERSHIP(tenancyId)
    );

    return result.data;
};

export const getFreeTenants = async (searchText: string): Promise<FreeTenantLookUp[]> => {
    const result = await httpGet<FreeTenantLookUp[]>(
        window.APP_SETTINGS.spaceStoneApiBaseUri,
        urls.LOOKUP.FREE_TENANTS,
        { params: { searchText } }
    );

    return result.data;
};
